import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Clapperboard, Menu, X } from "lucide-react";

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);
  if (loading) return <div className="h-screen flex items-center justify-center bg-white text-zinc-500 text-sm">Loading…</div>;
  if (!user) return null;
  return children;
}

const NAV = [
  { to: "/dashboard", label: "Reactions", icon: LayoutGrid },
];

export default function AppShell({ children }) {
  const { logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  return (
    <div className="min-h-screen bg-white text-zinc-900 flex flex-col">
      {/* Top tab bar */}
      <header className="sticky top-0 z-50 bg-zinc-950 text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-8">
          <Link to="/" className="flex items-center shrink-0" data-testid="shell-logo">
            <Clapperboard size={28} className="text-rose-500 mr-2" />
            <span className="font-display font-bold text-2xl">Reaction <span className="text-orange-500">Video</span></span>
          </Link>

          {/* Desktop tabs + logout (right group) */}
          <div className="ml-auto hidden md:flex items-center gap-5">
            <nav className="flex items-center gap-5">
              {NAV.map(({ to, label }) => (
                <Link key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors ${isActive(to) ? "text-white" : "text-zinc-300 hover:text-white"}`}>
                  {label}
                </Link>
              ))}
            </nav>
            <button onClick={logout} data-testid="logout-btn" className="text-sm font-medium rounded-[4px] px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white transition-colors">
              Logout
            </button>
          </div>

          {/* Mobile menu toggle */}
          <button onClick={() => setMenuOpen((v) => !v)} data-testid="nav-menu-toggle"
            className="ml-auto md:hidden text-zinc-300 hover:text-white p-1.5 rounded-[4px] hover:bg-zinc-900 transition-colors">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <div className="md:hidden border-t border-zinc-800 px-4 py-3 space-y-3" data-testid="mobile-nav">
            {NAV.map(({ to, label }) => (
              <Link key={to} to={to} data-testid={`mobile-nav-${label.toLowerCase()}`}
                className={`block text-sm font-medium transition-colors ${isActive(to) ? "text-white" : "text-zinc-300 hover:text-white"}`}>
                {label}
              </Link>
            ))}
            <button onClick={logout} data-testid="logout-btn-mobile" className="w-full text-sm font-medium rounded-[4px] px-3 py-2.5 bg-red-600 hover:bg-red-700 text-white transition-colors">
              Logout
            </button>
          </div>
        )}
      </header>

      <main className="flex-1 min-h-0 bg-white text-zinc-900">
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
